import React, { useState } from 'react';
import {
    ServiceConfigurator,
    SmriqConfigurator,
    AddOnConfigurator,
    ServicePlanSummary,
    isSmriqProduct,
    useAvailableAddOns,
} from '@features/service-plans';
import { useQuote } from '../../context/QuoteContext';
import { useQuoteBasketPricing } from '../../hooks/useBasketPricing';
import { PageHeader } from '@/shared/components';
import { DefaultScheduleSummaryCard } from '../DefaultScheduleSummary/DefaultScheduleSummaryCard';
import { configurationSubtitle } from '../../utils/configurationSubtitle';

export const ConfigurationStep: React.FC = () => {
    const {
        selectedProduct,
        configuredServices,
        updateServices,
        configuredAddOns,
        updateAddOns,
        canProceedToStep,
        paymentSelection,
    } = useQuote();
    // Gated before the call, not after: the placeholder below renders no figures, so pricing
    // a basket behind an unmet gate is a request nothing displays and no failure surfaces.
    const isConfigurable = canProceedToStep('configuration') && !!selectedProduct;
    const pricing = useQuoteBasketPricing(isConfigurable);
    const optionalExtras = useAvailableAddOns(selectedProduct?.id);

    // SMRiQ rebuilds its service order (visits) before the basket re-prices; lifting that loading
    // state lets the summary cascade its loaders from the moment the regime starts updating.
    const [serviceOrderUpdating, setServiceOrderUpdating] = useState(false);

    // Defensive checks to ensure arrays are always defined
    const safeConfiguredServices = Array.isArray(configuredServices) ? configuredServices : [];
    const safeConfiguredAddOns = Array.isArray(configuredAddOns) ? configuredAddOns : [];

    // `|| !selectedProduct` narrows the type for the render below; it is not a second gate.
    if (!isConfigurable || !selectedProduct) {
        return (
            <div className="text-center py-12">
                <div className="text-mp-muted mb-4">
                    <svg
                        className="mx-auto h-12 w-12"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                    </svg>
                </div>
                <h3 className="text-lg font-medium text-mp-foreground mb-2">Previous Steps Required</h3>
                <p className="text-mp-muted">
                    Please complete the previous steps before configuration.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap justify-between content-start items-start">
                <PageHeader
                    title={`Configure ${selectedProduct.name}`}
                    subtitle={configurationSubtitle(optionalExtras)}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
                <div className="min-w-0 space-y-8">
                    {isSmriqProduct(selectedProduct) ? (
                        <SmriqConfigurator
                            setConfiguredServices={updateServices}
                            onServiceOrderLoadingChange={setServiceOrderUpdating}
                        />
                    ) : (
                        <ServiceConfigurator
                            setConfiguredServices={updateServices}
                            pricing={pricing.isUpdating ? undefined : pricing.data}
                        />
                    )}

                    <AddOnConfigurator
                        configuredServices={safeConfiguredServices}
                        configuredAddOns={safeConfiguredAddOns}
                        setConfiguredAddOns={updateAddOns}
                    />
                </div>
                {/* --- Summary Section --- */}
                <div className="min-w-0">
                    <ServicePlanSummary
                        configuredServices={safeConfiguredServices}
                        configuredAddOns={safeConfiguredAddOns}
                        pricing={pricing}
                        isServiceOrderUpdating={serviceOrderUpdating}
                        paymentSummary={
                            <DefaultScheduleSummaryCard
                                product={selectedProduct}
                                configuredServices={safeConfiguredServices}
                                configuredAddOns={safeConfiguredAddOns}
                                pricing={pricing}
                                paymentSelection={paymentSelection}
                                isServiceOrderUpdating={serviceOrderUpdating}
                            />
                        }
                    />
                </div>
            </div>
        </div>
    );
};
